/**
 * Propagates viewport *actions* — window width/level, zoom and pan — from the
 * viewport being interacted with to the others, at the scope a hanging
 * protocol's `syncActions` asks for.
 *
 * The sibling of StackScrollSyncService, and deliberately built to the same
 * shape: the two settings are independent axes of the same idea. Scroll linking
 * syncs *where you are* in a stack; this syncs *what you do* to the image. A
 * protocol can ask for either, both, or neither.
 *
 * What propagates is what Cornerstone gives us a synchronizer for: VOI (window
 * width/level, plus invert and colormap, which ride the same event) and camera
 * zoom/pan. Zoom is copied as a relative factor rather than a parallel scale, so
 * viewports showing series of different pixel dimensions stay sensible.
 * Rotation and flip have no synchronizer and are NOT propagated.
 *
 * Pan is withheld from viewports holding an alignment lock. In this viewer an
 * alignment IS a pan - setStackPosition pins an image against a canvas edge by
 * panning the camera - so a locked viewport that shares its pan cannot keep a
 * side of its own. A mammography protocol asking for right-and-lock on one column
 * and left-and-lock on the other would otherwise end up with every image on
 * whichever side wrote its camera last. Zoom and windowing carry no such meaning
 * and keep propagating across the whole scope.
 */

import { getAllViewports } from "./CornerstoneViewportService";
import { getScrollSyncedViewportIndices } from "./StackScrollSyncService";
import { getEnabledElement } from "@cornerstonejs/core";
import { SynchronizerManager } from "@cornerstonejs/tools";
import {
  createVOISynchronizer,
  createZoomPanSynchronizer,
} from "@cornerstonejs/tools/synchronizers";
import { ACTION_SYNC_MODES } from "../../../constants/Constants";

let ACTION_SYNC_GROUPS_INFO = [];
let ACTION_SYNC_ENABLE_REQUEST_ID = 0;

const clearSyncGroups = (invalidateInFlight = true) => {
  if (invalidateInFlight) {
    ACTION_SYNC_ENABLE_REQUEST_ID++;
  }

  ACTION_SYNC_GROUPS_INFO.forEach(({ viewports, synchronizerId }) => {
    viewports.forEach(({ viewportId, renderingEngineId }) => {
      removeViewportFromSyncGroup(viewportId, renderingEngineId, synchronizerId);
    });
  });

  ACTION_SYNC_GROUPS_INFO = [];
};

const getOrCreateSynchronizer = (id, create) => {
  let synchronizer = SynchronizerManager.getSynchronizer(id);

  if (!synchronizer) {
    synchronizer = create(id);
  }

  return synchronizer;
};

const getViewportIndex = (id) => {
  const match = String(id).match(/(\d+)$/);
  return match ? Number(match[1]) : -1;
};

/**
 * The viewport indices an action should reach, for each scope.
 *
 * Returns null when the scope cannot be resolved — an unknown mode, or a scope
 * whose defining input is missing (the active viewport has no series, nothing is
 * linked). Null means "sync nothing", which is different from "sync everything":
 * a protocol that asked for same-series scoping should not silently widen to all
 * viewports when the series is unknown.
 */
const viewportIndicesForMode = (
  mode,
  activeViewportIndex,
  viewportSeriesData,
  linkedViewportIndices
) => {
  const loadedIndices = (viewportSeriesData ?? [])
    .map((data, index) => ({ data, index }))
    .filter(({ data }) => data?.stack && data.stack !== "")
    .map(({ index }) => index);

  switch (mode) {
    case ACTION_SYNC_MODES.ALL:
      return loadedIndices;

    case ACTION_SYNC_MODES.SERIES: {
      const activeSeries = viewportSeriesData?.[activeViewportIndex]?.series;
      if (activeSeries === undefined || activeSeries === null || activeSeries === "") {
        return null;
      }
      return loadedIndices.filter(
        (index) => viewportSeriesData[index]?.series === activeSeries
      );
    }

    case ACTION_SYNC_MODES.LINKED: {
      // The radiologist's own chain-icon selection when there is one, otherwise
      // whatever the scroll synchronizer has grouped — see
      // getScrollSyncedViewportIndices for why that is the honest reading.
      const linked =
        linkedViewportIndices?.length > 0
          ? linkedViewportIndices
          : getScrollSyncedViewportIndices();
      if (!linked.length) return null;
      return loadedIndices.filter((index) => linked.includes(index));
    }

    default:
      return null;
  }
};

/**
 * Register action synchronization across the viewports the mode scopes to.
 *
 * Registration is passive: it propagates on the next VOI/camera event, and does
 * nothing to the viewports as they stand. Callers rely on that — a hang applies
 * its own per-viewport windowing and alignment first, and registering while that
 * is in flight would let the first write flatten every viewport.
 *
 * @param {number} activeViewportIndex - the viewport the scope is measured from
 * @param {string} mode - ACTION_SYNC_MODES value
 * @param {Array} viewportSeriesData - per-viewport series data, indexed by viewport
 * @param {Array} linkedViewportIndices - hand-linked viewports, or null
 * @param {Array} alignmentLockedIndices - viewports holding an alignment lock,
 *        which take part in everything but pan
 */
export const enableActionSync = async (
  activeViewportIndex,
  mode = ACTION_SYNC_MODES.DISABLED,
  viewportSeriesData = [],
  linkedViewportIndices = null,
  alignmentLockedIndices = []
) => {
  const requestId = ++ACTION_SYNC_ENABLE_REQUEST_ID;

  if (!mode || mode === ACTION_SYNC_MODES.DISABLED) {
    await disableActionSync();
    return;
  }

  // Rebuilt from a clean slate, like stack-scroll sync: this is called again on
  // every load, drop and active-viewport change, and partially-updated groups
  // would leave viewports syncing to series they no longer show.
  clearSyncGroups(false);

  // Resolved after the await, not before: the LINKED scope reads the stack-scroll
  // groups, and the effects that (re)build those are kicked off in the same tick
  // as this call. Reading first would see the groups as they were a beat ago.
  let viewports = await getAllViewports();

  const scopedIndices = viewportIndicesForMode(
    mode,
    activeViewportIndex,
    viewportSeriesData,
    linkedViewportIndices
  );

  if (!scopedIndices || scopedIndices.length < 2) {
    return;
  }

  if (requestId !== ACTION_SYNC_ENABLE_REQUEST_ID) {
    return;
  }

  viewports = viewports
    .filter(
      (viewport) =>
        viewport.getCurrentImageId() !== undefined &&
        viewport.type === "stack" &&
        !viewport.id.startsWith("thumbnail")
    )
    .filter((viewport) => scopedIndices.includes(getViewportIndex(viewport.id)))
    // Keep ordering deterministic so synchronizer IDs stay stable.
    .sort((a, b) => a.id.localeCompare(b.id));

  if (requestId !== ACTION_SYNC_ENABLE_REQUEST_ID) {
    return;
  }

  const synchronizedViewports = viewports.map((viewport) => {
    const { id: viewportId, element } = viewport;
    const { renderingEngineId } = getEnabledElement(element);
    return { viewportId, renderingEngineId };
  });

  // A group of one has nothing to propagate to.
  if (synchronizedViewports.length < 2) {
    return;
  }

  const groupKey = synchronizedViewports.map(({ viewportId }) => viewportId).join(",");

  addSyncGroup(`voiSync_${groupKey}`, createVOISynchronizer, synchronizedViewports);

  // The viewports free to be moved sideways: everything not holding an alignment
  // lock. With no lock in the scope that is the whole group, and the registration
  // is the one it always was.
  const lockedIndices = new Set(alignmentLockedIndices ?? []);
  const panViewports = synchronizedViewports.filter(
    ({ viewportId }) => !lockedIndices.has(getViewportIndex(viewportId))
  );

  if (panViewports.length === synchronizedViewports.length) {
    addSyncGroup(`zoomPanSync_${groupKey}`, createZoomPanSynchronizer, synchronizedViewports);
    return;
  }

  // Zoom across the whole scope, pan only among the unlocked viewports. Two
  // synchronizers rather than one carrying per-viewport options, because those
  // options are read from the *target*: a locked viewport left in a pan-carrying
  // group would still push its own re-pin onto everything else in it.
  addSyncGroup(`zoomSync_${groupKey}`, createZoomPanSynchronizer, synchronizedViewports, {
    syncPan: false,
  });

  // A group of one has nothing to propagate to, and a hang whose every viewport
  // is locked - the mammography protocols - leaves none at all.
  if (panViewports.length < 2) {
    return;
  }

  const panGroupKey = panViewports.map(({ viewportId }) => viewportId).join(",");
  addSyncGroup(`panSync_${panGroupKey}`, createZoomPanSynchronizer, panViewports, {
    syncZoom: false,
  });
};

export const disableActionSync = async () => {
  clearSyncGroups(true);

  return;
};

const addSyncGroup = (synchronizerId, create, synchronizedViewports, options = {}) => {
  const synchronizer = getOrCreateSynchronizer(synchronizerId, create);

  synchronizedViewports.forEach(({ viewportId, renderingEngineId }) => {
    synchronizer.add({ viewportId, renderingEngineId });
    // Written for every member, not only the restricted ones: synchronizers are
    // reused across registrations by id, and their per-viewport options outlive
    // the group that set them.
    synchronizer.setOptions?.(viewportId, options);
  });

  ACTION_SYNC_GROUPS_INFO.push({ synchronizerId, viewports: synchronizedViewports });
};

const removeViewportFromSyncGroup = (viewportId, renderingEngineId, syncGroupId) => {
  const synchronizers = SynchronizerManager.getAllSynchronizers();

  const filteredSynchronizers = syncGroupId
    ? synchronizers.filter((s) => s.id === syncGroupId)
    : synchronizers;

  filteredSynchronizers.forEach((synchronizer) => {
    if (!synchronizer) {
      return;
    }

    synchronizer.remove({ viewportId, renderingEngineId });

    // Drop the group once the last viewport leaves it.
    if (
      !synchronizer.getSourceViewports().length &&
      !synchronizer.getTargetViewports().length
    ) {
      SynchronizerManager.destroySynchronizer(synchronizer.id);
    }
  });
};
